import type { Vetor3 } from '../geometria/vetor3'
import type { ObjetoTabletop } from './tipos'

/**
 * Pose de um veículo no plano do solo do Table top: posição (X/Z do mundo) e
 * guinada (rotação em torno de Y). O rolamento que separa `sobreRodas` de
 * `tombado.../capotado` é resolvido por `posicaoFinal.ts` para desenhar o
 * carro — aqui só interessa a "pegada" no chão, que é o que basta para ligar
 * uma vítima à âncora certa ou associar um equipamento ao veículo mais perto.
 *
 * `espelharX` é verdadeiro para `tombadoDireito`: os cálculos de âncora e de
 * zona de estabilização do domínio didático assumem o tombamento clássico
 * para a esquerda (`colocacaoNaLateralBaixa`); no lado direito, o mesmo
 * cálculo vale espelhando X antes de girar.
 */
export interface PosoVeiculoMundo {
  readonly x: number
  readonly z: number
  readonly guinadaRad: number
  readonly espelharX: boolean
}

export interface PontoPlano {
  readonly x: number
  readonly z: number
}

export function posoDoObjeto(objeto: ObjetoTabletop): PosoVeiculoMundo {
  return {
    x: objeto.transformacao.posicao[0],
    z: objeto.transformacao.posicao[2],
    guinadaRad: objeto.transformacao.rotacao[1],
    espelharX: objeto.veiculo?.posicaoFinal === 'tombadoDireito',
  }
}

/** Ponto do mundo → coordenadas locais do veículo (origem no centro do carro, +Z para a frente). */
export function mundoParaLocal(ponto: PontoPlano, poso: PosoVeiculoMundo): PontoPlano {
  const dx = ponto.x - poso.x
  const dz = ponto.z - poso.z
  const cos = Math.cos(-poso.guinadaRad)
  const sin = Math.sin(-poso.guinadaRad)
  const local = { x: dx * cos - dz * sin, z: dx * sin + dz * cos }
  return poso.espelharX ? { x: -local.x, z: local.z } : local
}

/** Coordenadas locais do veículo → ponto do mundo. Inversa exata de `mundoParaLocal`. */
export function localParaMundo(ponto: PontoPlano, poso: PosoVeiculoMundo): PontoPlano {
  const espelhado = poso.espelharX ? { x: -ponto.x, z: ponto.z } : ponto
  const cos = Math.cos(poso.guinadaRad)
  const sin = Math.sin(poso.guinadaRad)
  return {
    x: poso.x + (espelhado.x * cos - espelhado.z * sin),
    z: poso.z + (espelhado.x * sin + espelhado.z * cos),
  }
}

/** Distância no plano do solo, no mundo — usada para achar o veículo mais próximo de um ponto. */
export function distanciaPlano(a: PontoPlano, b: PontoPlano): number {
  return Math.hypot(a.x - b.x, a.z - b.z)
}

/**
 * Converte um ponto ANATÔMICO local (como `colocacaoNaConfiguracao` devolve,
 * no referencial não-rolado do veículo — a mesma convenção usada por
 * Estabilização e Extração) para o mundo, considerando a rolagem e a guinada
 * REAIS do objeto (não recalculadas de `posicaoFinal`, para acompanhar um
 * gizmo manual sem exigir que o aluno reaplique a posição final).
 *
 * A rolagem gira o plano X/Y (lateral/vertical) em torno do eixo Z
 * (longitudinal) — é como o resto da cena já representa "tombar" ou
 * "capotar" um veículo — e só depois a guinada gira o resultado em torno de
 * Y e soma a posição do veículo no mundo. `tombadoDireito` espelha X do
 * ponto ANTES de rolar, pelo mesmo motivo de `mundoParaLocal`.
 */
export function pontoMundoNoVeiculo(pontoLocal: Vetor3, objeto: ObjetoTabletop): Vetor3 {
  const espelhar = objeto.veiculo?.posicaoFinal === 'tombadoDireito'
  const base = espelhar ? { x: -pontoLocal.x, y: pontoLocal.y, z: pontoLocal.z } : pontoLocal

  // A pose 3D usa Euler YXZ (guinada no eixo vertical do mundo). Sem guinada, isso aplica a rolagem Z e depois
  // a inclinação X ao ponto local. Essa ordem é importante no capotado: com
  // inclinação positiva, o cofre (+Z) baixa e a traseira se eleva.
  const rollZ = objeto.transformacao.rotacao[2]
  const cosR = Math.cos(rollZ)
  const sinR = Math.sin(rollZ)
  const rolado = {
    x: base.x * cosR - base.y * sinR,
    y: base.x * sinR + base.y * cosR,
    z: base.z,
  }

  const pitchX = objeto.transformacao.rotacao[0]
  const cosP = Math.cos(pitchX)
  const sinP = Math.sin(pitchX)
  const inclinado = {
    x: rolado.x,
    y: rolado.y * cosP - rolado.z * sinP,
    z: rolado.y * sinP + rolado.z * cosP,
  }

  const poso = { ...posoDoObjeto(objeto), espelharX: false }
  const plano = localParaMundo({ x: inclinado.x, z: inclinado.z }, poso)
  return { x: plano.x, y: objeto.transformacao.posicao[1] + inclinado.y, z: plano.z }
}

/**
 * O veículo cuja "pegada" no chão está mais perto de `ponto`, dentre os
 * objetos com bloco `veiculo`, ou `null` se a cena não tiver nenhum.
 */
export function veiculoMaisProximo(
  ponto: PontoPlano,
  objetos: readonly ObjetoTabletop[],
): ObjetoTabletop | null {
  let melhor: ObjetoTabletop | null = null
  let menorDistancia = Number.POSITIVE_INFINITY
  for (const objeto of objetos) {
    if (!objeto.veiculo) continue
    const distancia = distanciaPlano(ponto, posoDoObjeto(objeto))
    if (distancia < menorDistancia) {
      menorDistancia = distancia
      melhor = objeto
    }
  }
  return melhor
}

